import { useEffect, useMemo, useState } from 'react';
import { AddButton, LiveScreen, QueryState, StatusBadge, StatusPill, formatDateTime, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { SpecRecordDrawer } from '@/components/ChildRecords';
import { useRows } from '@/hooks/useResource';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { registerTemplates, sendTemplateTest, templateStatus, type TemplateStatus } from '@/api/whatsappTemplates';
import { htmlToPdf } from '@/lib/pdf';
import { toast } from '@/lib/toast';

interface MessageRow {
  id: string; channel: string; recipient_masked: string | null; template_code: string | null; use_case: string | null;
  provider_name: string | null; is_mock: boolean; status: string; sent_at: string | null; delivered_at: string | null;
  failure_reason: string | null; created_at: string;
}
interface CodeRow {
  id: string; purpose: string; channel: string; destination_masked: string | null; expires_at: string; verified_at: string | null;
  attempt_count: number; max_attempts: number; status: string; created_at: string;
}
interface TemplateRow { id: string; channel: string; template_code: string; use_case: string | null; language_code: string | null; provider_template_id: string | null; body_template: string | null; status: string }
interface ProviderRow { id: string; provider_type: string; provider_name: string; configuration_reference: string; is_mock: boolean; status: string; messages_30d: number }

const CHANNEL: Record<string, string> = { whatsapp: 'WhatsApp', sms: 'SMS', email: 'Email', voice: 'Voice', in_app: 'In app' };
const channel = (value: string) => CHANNEL[value] ?? value;

/** The three providers the edge functions know how to call, and the secrets each reads. */
const KNOWN = [
  { id: 'Resend', label: 'Resend (email)', type: 'email', reference: 'RESEND_API_KEY,RESEND_FROM' },
  { id: 'MSG91 (DLT registered)', label: 'MSG91 (SMS)', type: 'sms', reference: 'MSG91_AUTH_KEY,MSG91_TEMPLATE_DEFAULT' },
  { id: 'WhatsApp Cloud API (S360 sender)', label: 'WhatsApp Cloud API', type: 'whatsapp', reference: 'WHATSAPP_ACCESS_TOKEN,WHATSAPP_PHONE_NUMBER_ID' },
];

type Tab = 'messages' | 'codes' | 'templates' | 'whatsapp' | 'providers';

function Messages() {
  const [stat, setStat] = useState<string | null>(null);
  const rows = useRows<MessageRow>('message_log', { orderBy: { column: 'created_at', ascending: false } });
  const STATS = useMemo(() => [
    { key: 'all', label: 'Messages', tone: 'neutral' as const, match: () => true },
    { key: 'sent', label: 'Sent or delivered', tone: 'ok' as const, match: (row: MessageRow) => ['sent', 'delivered', 'read'].includes(row.status) },
    { key: 'queued', label: 'Waiting', tone: 'info' as const, match: (row: MessageRow) => ['queued', 'sending'].includes(row.status) },
    { key: 'failed', label: 'Failed', tone: 'error' as const, match: (row: MessageRow) => ['failed', 'refused'].includes(row.status) },
  ], []);
  const columns: Array<Column<MessageRow>> = [
    { key: 'created_at', label: 'When', render: (row) => formatDateTime(row.created_at) },
    { key: 'channel', label: 'Channel', render: (row) => channel(row.channel) },
    { key: 'recipient_masked', label: 'To', render: (row) => row.recipient_masked ?? '--' },
    { key: 'use_case', label: 'What', render: (row) => <span className="cell-stack"><span>{(row.use_case ?? '--').replace(/_/g, ' ')}</span>{row.template_code && <small className="muted">{row.template_code}</small>}</span> },
    { key: 'provider_name', label: 'Provider', render: (row) => <span className="cell-stack"><span>{row.provider_name ?? '--'}</span>{row.is_mock && <small className="muted">mock - not really sent</small>}</span> },
    { key: 'status', label: 'Status', render: (row) => <span className="cell-stack"><StatusPill value={row.status} />{row.failure_reason && <small className="muted">{row.failure_reason}</small>}</span> },
  ];
  return (
    <>
      <StatCards active={stat} onSelect={setStat} rows={rows.data ?? []} stats={STATS} />
      <QueryState empty="Nothing has been sent yet." query={rows}>
        <DataGrid columns={columns} exportName="message-log" rows={applyStat(rows.data ?? [], STATS, stat)} screenKey="comms.log" />
      </QueryState>
    </>
  );
}

function Codes() {
  const rows = useRows<CodeRow>('otp_log', { orderBy: { column: 'created_at', ascending: false } });
  const columns: Array<Column<CodeRow>> = [
    { key: 'created_at', label: 'When', render: (row) => formatDateTime(row.created_at) },
    { key: 'purpose', label: 'For', render: (row) => row.purpose.replace(/_/g, ' ') },
    { key: 'channel', label: 'Channel', render: (row) => channel(row.channel) },
    { key: 'destination_masked', label: 'To', render: (row) => row.destination_masked ?? '--' },
    { key: 'attempt_count', label: 'Tries', align: 'right', render: (row) => `${row.attempt_count} / ${row.max_attempts}` },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.verified_at ? 'verified' : row.status} /> },
  ];
  return (
    <QueryState empty="No one-time code has been issued yet." query={rows}>
      <DataGrid columns={columns} exportName="one-time-codes" rows={rows.data ?? []} screenKey="comms.log" />
    </QueryState>
  );
}

function Templates() {
  const rows = useRows<TemplateRow>('message_templates', { orderBy: { column: 'template_code' } });
  const columns: Array<Column<TemplateRow>> = [
    { key: 'template_code', label: 'Template', render: (row) => <span className="cell-stack"><strong>{row.template_code}</strong><small className="muted">{(row.use_case ?? '').replace(/_/g, ' ')}</small></span> },
    { key: 'channel', label: 'Channel', render: (row) => channel(row.channel) },
    { key: 'language_code', label: 'Language', render: (row) => row.language_code ?? '--' },
    { key: 'provider_template_id', label: 'Registered as', render: (row) => row.provider_template_id ?? <span className="muted">not registered</span> },
    { key: 'body_template', label: 'Wording', render: (row) => <span className="muted">{row.body_template ?? '--'}</span> },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
  ];
  return (
    <>
      <p className="muted">WhatsApp wording is approved by Meta and SMS wording is registered under DLT, so a template is changed with the provider first, never here.</p>
      <QueryState empty="No message templates." query={rows}>
        <DataGrid columns={columns} exportName="message-templates" rows={rows.data ?? []} screenKey="comms.template.master" />
      </QueryState>
    </>
  );
}

const LANGUAGE_LABEL: Record<string, string> = { en: 'English', hi: 'Hindi', bn: 'Bengali', te: 'Telugu', ta: 'Tamil', kn: 'Kannada', gu: 'Gujarati' };
const META_STATUS: Record<string, { label: string; tone: 'green' | 'amber' | 'red' | 'gray' }> = {
  APPROVED: { label: 'Approved', tone: 'green' }, PENDING: { label: 'Pending', tone: 'amber' }, IN_APPEAL: { label: 'In appeal', tone: 'amber' },
  REJECTED: { label: 'Rejected', tone: 'red' }, PAUSED: { label: 'Paused', tone: 'red' }, DISABLED: { label: 'Disabled', tone: 'red' },
  MISSING: { label: 'Not registered', tone: 'gray' },
};
const TEST_TEMPLATES = [
  { key: 'consent_document', label: 'Consent document (draft)' }, { key: 'consent_final_document', label: 'Consent document (signed)' },
  { key: 'sale_invoice', label: 'Sale invoice (draft)' }, { key: 'sale_invoice_final', label: 'Sale invoice (final)' },
];

/** A file as base64, without the data: prefix. */
const base64Of = (file: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).replace(/^data:[^;]*;base64,/, ''));
  reader.onerror = () => reject(new Error('The file could not be read.'));
  reader.readAsDataURL(file);
});

/**
 * WhatsApp registration (owner, 2026-10-04): what Meta holds against the seven-language catalogue,
 * a button to register what is missing, and a test of the document send to the signed-in person's
 * own WhatsApp and email. Only the platform administrator sees anything but the matrix.
 */
function WhatsApp() {
  const { profile } = useAuth();
  const [status, setStatus] = useState<TemplateStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [channels, setChannels] = useState<Array<'whatsapp' | 'email'>>(['email', 'whatsapp']);
  const [templateKey, setTemplateKey] = useState('consent_document');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const mayManage = can(profile, 'comms.provider', 'update');

  async function refresh() {
    setLoading(true); setError(null);
    try { setStatus(await templateStatus()); } catch (failure) { setError(failure instanceof Error ? failure.message : 'The status could not be read.'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void refresh(); }, []);

  async function register() {
    setRegistering(true);
    try {
      let registered = 0; let failed = 0; let remaining = 1; let rounds = 0; const errors: string[] = [];
      while (remaining > 0 && rounds < 6) {
        const outcome = await registerTemplates(25);
        registered += outcome.registered; failed += outcome.failed; remaining = outcome.remaining; rounds += 1;
        errors.push(...outcome.results.filter((row) => !row.ok).map((row) => `${row.name} (${row.language}): ${row.error}`));
        if (outcome.failed > 0 && outcome.registered === 0) break;
      }
      if (registered) toast.success(`${registered} template${registered === 1 ? '' : 's'} sent to Meta for approval.`);
      if (failed) toast.error(`${failed} refused: ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '; ...' : ''}`);
      if (!registered && !failed) toast.success('Nothing to register: Meta already holds every template.');
      await refresh();
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : 'Registration failed.');
    } finally { setRegistering(false); }
  }

  async function test() {
    if (channels.length === 0) { toast.error('Tick WhatsApp or email.'); return; }
    setSending(true);
    try {
      let pdfBase64: string | undefined; let filename: string | undefined;
      if (file) { pdfBase64 = await base64Of(file); filename = file.name; }
      else {
        const made = await htmlToPdf(`<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;font:14px/1.5 system-ui,sans-serif;background:#e5e7eb}
.page{width:794px;height:1123px;margin:0 auto;padding:60px 64px;background:#fff;position:relative}h1{font-size:22px;margin:0 0 8px}p{margin:0 0 10px}</style></head>
<body><section class="page"><h1>Vanam test document</h1><p>Sent from the Communications page on ${new Date().toLocaleString('en-GB')} to check that a PDF reaches WhatsApp and email the way a farmer's consent would.</p>
<p>It carries no record. If you were not expecting it, ignore it.</p></section></body></html>`, 'vanam-test.pdf');
        pdfBase64 = await base64Of(made); filename = made.name;
      }
      const outcome = await sendTemplateTest({ channels, templateKey, pdfBase64, filename });
      const label = (row: (typeof outcome.results)[number]) => `${row.channel === 'email' ? 'email' : 'WhatsApp'} (${row.destination_masked}, ${LANGUAGE_LABEL[row.language] ?? row.language})`;
      const went = outcome.results.filter((row) => row.delivered);
      const failed = outcome.results.filter((row) => !row.delivered);
      if (went.length) toast.success(`Sent to your ${went.map(label).join(' and ')}.`);
      if (failed.length) toast.error(`Not sent to ${failed.map((row) => `${label(row)}: ${row.error}`).join('; ')}`);
    } catch (failure) {
      toast.error(failure instanceof Error ? failure.message : 'The test could not be sent.');
    } finally { setSending(false); }
  }

  const languages = status ? [...new Set(status.rows.map((row) => row.language))] : [];
  const names = status ? [...new Set(status.rows.map((row) => row.name))] : [];
  const cell = (name: string, language: string) => status?.rows.find((row) => row.name === name && row.language === language);
  const missing = status?.rows.filter((row) => row.status === 'MISSING').length ?? 0;
  const pending = status?.rows.filter((row) => row.status === 'PENDING').length ?? 0;
  const approved = status?.rows.filter((row) => row.status === 'APPROVED').length ?? 0;
  const notOn = status?.languages_ready.filter((lang) => !status.languages_switched_on.includes(lang)) ?? [];

  return (
    <>
      <p className="muted">
        Every WhatsApp message a farmer or buyer is sent is a template Meta has approved, in the language the person reads. The wording lives in
        the catalogue (<code>docs/message_templates</code>); registering sends it to Meta, which approves each language on its own. Until a language
        is approved and switched on, that person is sent English.
      </p>
      {error && <p className="field-error">{error}</p>}
      {status && (
        <>
          <div className="toolbar-row">
            <span className="muted">Sender <strong>{status.phone_number_id}</strong> · account <strong>{status.waba_id}</strong> · {approved} approved, {pending} pending, {missing} not registered.</span>
            <button disabled={loading} onClick={() => void refresh()} type="button">{loading ? 'Reading...' : 'Refresh'}</button>
            {mayManage && <button className="primary" disabled={registering || missing === 0} onClick={() => void register()} type="button">
              {registering ? 'Registering...' : missing === 0 ? 'All registered' : `Register the ${missing} missing with Meta`}</button>}
          </div>
          <p className="muted" style={{ marginTop: 6 }}>
            Languages switched on: <strong>{status.languages_switched_on.map((lang) => LANGUAGE_LABEL[lang] ?? lang).join(', ')}</strong>.
            {notOn.length > 0 && <> Fully approved but not yet switched on: <strong>{notOn.map((lang) => LANGUAGE_LABEL[lang] ?? lang).join(', ')}</strong>: set the
              function secret <code>WHATSAPP_TEMPLATE_LANGUAGES={[...status.languages_switched_on, ...notOn].join(',')}</code>.</>}
          </p>
          <div className="line-table-wrap">
            <table className="simple-table">
              <thead><tr><th>Template</th>{languages.map((lang) => <th key={lang}>{LANGUAGE_LABEL[lang] ?? lang}</th>)}</tr></thead>
              <tbody>
                {names.map((name) => {
                  const first = status.rows.find((row) => row.name === name)!;
                  return (
                    <tr key={name}>
                      <td><span className="cell-stack"><strong>{name}</strong><small className="muted">{first.category === 'AUTHENTICATION' ? 'one-time code' : first.document ? 'with the PDF' : 'notice'}{first.keys.length > 1 ? ` · ${first.keys.join(', ')}` : ''}</small></span></td>
                      {languages.map((lang) => {
                        const row = cell(name, lang);
                        const meta = META_STATUS[row?.status ?? 'MISSING'] ?? { label: row?.status ?? '--', tone: 'gray' as const };
                        return <td key={lang} title={row?.rejected_reason ?? undefined}><StatusBadge label={meta.label} tone={meta.tone} />{row?.rejected_reason && row.rejected_reason !== 'NONE' && <small className="muted"> {row.rejected_reason}</small>}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {mayManage && (
        <section className="card" style={{ marginTop: 16 }}>
          <h3 className="section-heading">Send yourself a test</h3>
          <p className="muted">A document goes to your own WhatsApp number and email address, exactly as a farmer would receive it: the PDF attached
            to the email, and in the WhatsApp message's header. WhatsApp needs that template approved first.</p>
          <div className="toolbar-row">
            <label className="field"><span>Template</span>
              <select onChange={(event) => setTemplateKey(event.target.value)} value={templateKey}>
                {TEST_TEMPLATES.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
              </select></label>
            <label className="field"><span>PDF (optional; otherwise a one-page test document)</span>
              <input accept="application/pdf" onChange={(event) => setFile(event.target.files?.[0] ?? null)} type="file" /></label>
          </div>
          <div className="check-list">
            {(['whatsapp', 'email'] as const).map((channel) => (
              <label key={channel}><input checked={channels.includes(channel)} onChange={(event) => setChannels((now) => (event.target.checked ? [...now, channel] : now.filter((item) => item !== channel)))} type="checkbox" /> {channel === 'email' ? 'Email' : 'WhatsApp'}</label>
            ))}
          </div>
          <button className="primary" disabled={sending} onClick={() => void test()} type="button">{sending ? 'Sending...' : 'Send the test to me'}</button>
        </section>
      )}
    </>
  );
}

function Providers() {
  const { profile } = useAuth();
  const rows = useRows<ProviderRow>('channel_providers', { orderBy: { column: 'provider_type' } });
  const [open, setOpen] = useState<ProviderRow | 'new' | null>(null);
  const mayAdd = can(profile, 'comms.provider', 'create');
  const editing = open && open !== 'new' ? open : null;
  const columns: Array<Column<ProviderRow>> = [
    { key: 'provider_type', label: 'Channel', render: (row) => <strong>{channel(row.provider_type)}</strong> },
    { key: 'provider_name', label: 'Provider', render: (row) => <span className="cell-stack"><span>{row.provider_name}</span>{row.is_mock && <small className="muted">mock - nothing really leaves</small>}</span> },
    { key: 'configuration_reference', label: 'Secrets it reads', render: (row) => <code>{row.configuration_reference}</code> },
    { key: 'messages_30d', label: 'Last 30 days', align: 'right' },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge label={row.status === 'active' ? 'On' : 'Off'} tone={row.status === 'active' ? 'green' : 'gray'} /> },
  ];
  return (
    <>
      <p className="muted">
        Nothing leaves the platform on a channel until its provider is on. Switch one on only once its secrets are set - the names are listed against it.
      </p>
      <AddButton disabled={!mayAdd} label="Add provider" onClick={() => setOpen('new')} title={mayAdd ? undefined : 'Your role cannot add providers.'} />
      <QueryState empty="No provider yet, so only in-app alerts can be delivered." query={rows}>
        <DataGrid columns={columns} deleteView="channel_providers" exportName="channel-providers" onRowClick={(row) => setOpen(row)}
                  recordNoun="provider" rows={rows.data ?? []} screenKey="comms.provider" selectedId={editing?.id} />
      </QueryState>
      {open && (
        <SpecRecordDrawer<ProviderRow>
          key={editing?.id ?? 'new'} editing={editing} onClose={() => setOpen(null)} screenKey="comms.provider"
          table="comms.channel_providers" view="channel_providers"
          title={editing ? `${channel(editing.provider_type)} - ${editing.provider_name}` : 'Add provider'}
          subtitle={editing ? undefined : 'Added switched off. Switch it on once its secrets are set.'}
          deleteLabel={editing ? `provider ${editing.provider_name}` : ''}
          defaults={{ provider_name: 'Resend', provider_type: 'email', configuration_reference: 'RESEND_API_KEY,RESEND_FROM', status: 'inactive' }}
          toolbar={(form, setForm) => (editing ? null : (
            <div className="toolbar-row">
              {KNOWN.map((known) => (
                <button key={known.id} type="button" className={form.provider_name === known.id ? 'primary' : undefined}
                        onClick={() => setForm((now) => ({ ...now, provider_name: known.id, provider_type: known.type, configuration_reference: known.reference }))}>
                  {known.label}
                </button>
              ))}
            </div>
          ))}
          fields={[
            { key: 'provider_type', label: 'Channel', type: 'select', required: true,
              options: [{ id: 'email', label: 'Email' }, { id: 'sms', label: 'SMS' }, { id: 'whatsapp', label: 'WhatsApp' }] },
            { key: 'provider_name', label: 'Provider', type: 'text', required: true },
            { key: 'configuration_reference', label: 'Secrets it reads', type: 'text', required: true,
              hint: 'The NAMES of the function secrets, separated by commas. Never paste a key here.' },
            { key: 'status', label: 'Status', type: 'select', required: true,
              options: [{ id: 'inactive', label: 'Off' }, { id: 'active', label: 'On - send through this provider' }] },
          ]}
        />
      )}
    </>
  );
}

/**
 * Communications (`comms`, back from the prototypes on 2026-09-27): what was sent to whom, the
 * one-time codes issued, the approved message wording, and the providers that carry each channel.
 * A provider row is the gate comms.fn_channel_available checks before anything leaves; the
 * credentials behind it are function secrets, named here and never stored.
 */
export default function CommsScreen() {
  const { profile } = useAuth();
  const tabs: Array<{ key: Tab; label: string; show: boolean }> = [
    { key: 'messages', label: 'Messages', show: can(profile, 'comms.log', 'read') },
    { key: 'codes', label: 'One-time codes', show: can(profile, 'comms.log', 'read') },
    { key: 'templates', label: 'Templates', show: can(profile, 'comms.template.master', 'read') },
    { key: 'whatsapp', label: 'WhatsApp registration', show: can(profile, 'comms.provider', 'read') },
    { key: 'providers', label: 'Providers', show: can(profile, 'comms.provider', 'read') },
  ].filter((tab): tab is { key: Tab; label: string; show: boolean } => tab.show);
  const [tab, setTab] = useState<Tab>(tabs[0]?.key ?? 'messages');
  return (
    <LiveScreen subtitle="What was sent to whom, the codes issued, the approved wording, and the providers that carry each channel." title="Communications">
      <div className="tab-row" role="tablist">
        {tabs.map((item) => (
          <button aria-selected={tab === item.key} className={tab === item.key ? 'active' : ''} key={item.key}
                  onClick={() => setTab(item.key)} role="tab" type="button">{item.label}</button>
        ))}
      </div>
      {tab === 'messages' && <Messages />}
      {tab === 'codes' && <Codes />}
      {tab === 'templates' && <Templates />}
      {tab === 'whatsapp' && <WhatsApp />}
      {tab === 'providers' && <Providers />}
    </LiveScreen>
  );
}
